import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Edit2, Plus, Trash2 } from "lucide-react";
import AppModal from "../../components/AppModal";
import DataTable from "../../components/DataTable";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { confirmAction, notify } from "../../utils/toastAlert";

const emptyForm = {
  nome: "",
  preco: "",
};

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const showModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).show();
};

const Servicos = () => {
  const user = useSelector((state) => state.user.user);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchServicos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/servicos");
      const allServicos = response.data?.data || response.data || [];
      const companyServicos = allServicos.filter(
        (item) => Number(item.empresa_id) === Number(user?.empresa_id)
      );
      setServicos(companyServicos);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao carregar serviços.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.empresa_id) {
      fetchServicos();
    }
  }, [user?.empresa_id]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (item = selected) => {
    if (!item) return;
    setEditing(item);
    setForm({
      nome: item.nome || "",
      preco: item.preco || "",
    });
    showModal("servicoFormModal");
  };

  const payload = () => {
    const data = { ...form };
    if (user?.empresa_id) {
      data.empresa_id = user.empresa_id;
    }
    return data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (editing) {
        await api.post(`/servicos/${editing.id}`, payload());
        await notify("Serviço actualizado com sucesso.", "success");
      } else {
        await api.post("/servicos", payload());
        await notify("Serviço cadastrado com sucesso.", "success");
      }

      setForm(emptyForm);
      setEditing(null);
      closeModal("servicoFormModal");
      fetchServicos();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível guardar o serviço.", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selected) return;
    const question = await confirmAction({
      title: "Excluir serviço",
      text: `Deseja excluir o serviço ${selected.nome}?`,
      icon: "warning",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (!question.isConfirmed) return;

    try {
      setLoading(true);
      await api.delete(`/servicos/${selected.id}`);
      await notify("Serviço excluído com sucesso.", "success");
      setSelected(null);
      fetchServicos();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível excluir o serviço.", "error");
    } finally {
      setLoading(false);
    }
  };

  const canManage = user?.nivel === "admin";

  return (
    <ResourcePage
      title="Serviços"
      breadcrumb={["Gestão", "Serviços"]}
      actions={(
        <>
          <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="modal" data-bs-target="#servicoFormModal" onClick={openCreate} disabled={!canManage}>
            <Plus size={16} />
            Cadastrar
          </button>
          <button className="btn btn-light btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={() => openEdit()} disabled={!canManage || !selected}>
            <Edit2 size={16} />
            Editar
          </button>
          <button className="btn btn-light text-danger btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={confirmDelete} disabled={!canManage || !selected}>
            <Trash2 size={16} />
            Excluir
          </button>
        </>
      )}
    >
      <div className="card app-card bg-white">
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {!canManage && <div className="alert alert-warning py-2">Apenas administradores podem gerir serviços.</div>}

          <DataTable
            id="servicosDataTable"
            columns={["Nome", "Preço"]}
            rows={servicos}
            renderRow={(item) => (
              <tr key={item.id} className={selected?.id === item.id ? "table-active" : ""} onClick={() => setSelected(item)}>
                <td>{item.nome}</td>
                <td>{item.preco ? `${item.preco}` : "-"}</td>
              </tr>
            )}
          />
        </div>
      </div>

      <AppModal
        id="servicoFormModal"
        title={editing ? "Editar serviço" : "Cadastrar serviço"}
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary" form="servicoForm" disabled={loading || !canManage}>
              {loading ? "A guardar..." : "Guardar"}
            </button>
          </>
        )}
      >
        <form id="servicoForm" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="nome">Nome</label>
              <input className="form-control" id="nome" name="nome" value={form.nome} onChange={handleChange} required />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="preco">Preço</label>
              <input className="form-control" id="preco" name="preco" type="number" step="0.01" value={form.preco} onChange={handleChange} />
            </div>
          </div>
        </form>
      </AppModal>
    </ResourcePage>
  );
};

export default Servicos;
